import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { toast } from 'sonner';
import { Sentry } from '@/lib/sentry';

interface UseCreateAddonCheckoutResult {
  createAddonCheckout: (addonId: string) => Promise<void>;
  isCreating: boolean;
  error: string | null;
}

export function useCreateAddonCheckout(): UseCreateAddonCheckoutResult {
  const { currentOrganizationId } = useCurrentOrganization();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createAddonCheckout = async (addonId: string): Promise<void> => {
    if (isCreating) return;
    setError(null);
    setIsCreating(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        'lemonsqueezy-create-checkout',
        {
          body: {
            mode: 'addon',
            addonId,
            organizationId: currentOrganizationId,
          },
        },
      );

      if (invokeError) {
        throw new Error(invokeError.message || 'Errore chiamata edge function');
      }

      const url = (data as { url?: string })?.url;
      if (!url) {
        const errMsg = (data as { error?: string })?.error || 'Risposta inattesa dall\'API';
        throw new Error(errMsg);
      }

      window.location.assign(url);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Errore sconosciuto';
      setError(msg);
      const lsNotConfigured = /Variant LS non configurato|Lemon Squeezy non configurato/.test(msg);
      toast.error(
        lsNotConfigured ? 'Pacchetto non ancora attivo' : 'Impossibile aprire il checkout',
        {
          description: lsNotConfigured
            ? 'Il listino di vendita Lemon Squeezy per questo pacchetto non è ancora collegato. Stiamo finalizzando l\'integrazione — scrivi a hello@dryconfig.com se vuoi accesso anticipato.'
            : msg,
          duration: 6000,
        },
      );
      Sentry.captureException(e, {
        tags: { feature: 'lemonsqueezy-addon-checkout' },
        extra: { addonId, organizationId: currentOrganizationId },
      });
    } finally {
      setIsCreating(false);
    }
  };

  return { createAddonCheckout, isCreating, error };
}
